import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { type ControlTestAction, ControlTestEdit } from '@client/components/controls/control-test-edit';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { ICONS } from '@client/constants/icons';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useState } from 'react';
import { z } from 'zod';
import { ControlTestFilters } from './ControlTestFilters';
import { useControlTestColumns } from './columns';

const ControlTestSearchSchema = z.object({
    'control-test-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner'),
                    operator: FilterOperatorSchema,
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/controls/tests')({
    staticData: {
        ui: {
            title: 'Control tests',
            subtitle: 'View and manage control tests and their outcomes',
            icon: ICONS.controlTest,
        },
        breadcrumb: {
            title: 'Control tests',
        },
    },
    validateSearch: zodValidator(ControlTestSearchSchema),
    component: ControlTestSearch,
});

type ControlTestWhere = NonNullable<RouterInputs['control']['test']['listControlTests']['where']>;

function ControlTestSearch() {
    const { trpc } = useTrpc();
    const searchParams = Route.useSearch();
    const [showFailed, setShowFailed] = useState(false);
    const [controlTestAction, setControlTestAction] = useState<ControlTestAction>(['none']);
    const [filterBarWhere, setFilterBarWhere] = useState<ControlTestWhere>({});

    // Default: show only untested tests (outcome: null)
    const [queryParams, setQueryParams] = usePathState<RouterInputs['control']['test']['listControlTests']>(
        'queryParams',
        {
            limit: DEFAULT_PAGE_SIZE,
            orderBy: [{ nextTestDue: 'asc' }, { outcome: { sort: 'desc', nulls: 'last' } }],
            where: {
                OR: [{ outcome: null }],
            },
        },
    );

    const { data, isPending, refetch } = trpc.control.test.listControlTests.useQuery(queryParams);

    const columns = useControlTestColumns();

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: { position: 'both' },
        // The hidden open-items default in `where` is not a user filter; only the filter bar is.
        emptyState: {
            noRecordsTitle: showFailed ? 'No untested or failed control tests' : 'No untested control tests',
            hasActiveFilters: !isWhereClauseEmpty(filterBarWhere),
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'control-tests-table-columns',
            enableReordering: true,
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
    });

    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        const checked = e.target.checked;
        setShowFailed(checked);

        // Build where clause: AND all filter bar conditions with the outcome filter
        const outcomeFilter = {
            OR: [{ outcome: null }, ...(checked ? [{ outcome: 'FAIL' as const }] : [])],
        } satisfies ControlTestWhere;

        const whereConditions: ControlTestWhere[] = [];
        if (Object.keys(filterBarWhere).length > 0) {
            whereConditions.push(filterBarWhere);
        }
        whereConditions.push(outcomeFilter);

        setQueryParams((prev) => ({
            ...prev,
            where: whereConditions.length === 1 ? whereConditions[0] : { AND: whereConditions },
            offset: 0,
        }));
    };

    return (
        <PageController route={Route}>
            <ControlTestEdit
                action={controlTestAction}
                onClose={() => {
                    setControlTestAction(['none']);
                    refetch();
                }}
            />

            <TableToolbar
                filters={
                    <ControlTestFilters
                        initialFilters={searchParams['control-test-filters']}
                        onChange={(where) => {
                            setFilterBarWhere(where);

                            const outcomeFilter: ControlTestWhere = {
                                OR: [{ outcome: null }, ...(showFailed ? [{ outcome: 'FAIL' as const }] : [])],
                            };

                            const whereConditions: ControlTestWhere[] = [];
                            if (Object.keys(where).length > 0) {
                                whereConditions.push(where);
                            }
                            whereConditions.push(outcomeFilter);

                            setQueryParams((prev) => ({
                                ...prev,
                                where: whereConditions.length === 1 ? whereConditions[0] : { AND: whereConditions },
                                offset: 0,
                            }));
                        }}
                    />
                }
                actions={
                    <Checkbox checked={showFailed} onChange={handleCheckboxChange}>
                        Show failed tests
                    </Checkbox>
                }
            />

            <PrismaTable
                {...tableProps}
                scroll={{ x: 'max-content' }}
                onRow={({ id, controlId }) => ({
                    className: 'cursor-pointer',
                    onClick: () => {
                        setControlTestAction(['edit', id, controlId]);
                    },
                })}
            />
        </PageController>
    );
}
